import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { FaLock, FaCheckCircle, FaExclamationTriangle, FaShieldAlt } from 'react-icons/fa';
import { getXbetId, saveXbetId } from '../services/xbetService';

export default function XbetVerificationCard({
  username,
  initialXbetId = null,
  onVerified,
  compact = false,
}) {
  const [currentId, setCurrentId] = useState(initialXbetId || null);
  const [inputValue, setInputValue] = useState(initialXbetId || '');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!initialXbetId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const currentUser = username || localStorage.getItem('kick_user') || '';

  useEffect(() => {
    if (initialXbetId) {
      setCurrentId(initialXbetId);
      setInputValue(initialXbetId);
      setFetching(false);
      return;
    }

    if (!currentUser) {
      setFetching(false);
      return;
    }

    let isMounted = true;
    setFetching(true);
    getXbetId(currentUser)
      .then((data) => {
        if (!isMounted) return;
        if (data?.xbet_id) {
          const clean = String(data.xbet_id).trim();
          setCurrentId(clean);
          setInputValue(clean);
          if (onVerified) onVerified(clean);
        }
      })
      .catch((err) => console.error('Error al consultar 1XBET ID:', err))
      .finally(() => {
        if (isMounted) setFetching(false);
      });

    return () => {
      isMounted = false;
    };
  }, [currentUser, initialXbetId]);

  const handleOpenConfirm = (e) => {
    if (e) e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    const clean = String(inputValue || '').trim();
    if (!clean) {
      setErrorMsg('Debes ingresar tu 1XBET ID.');
      return;
    }
    if (!/^\d+$/.test(clean)) {
      setErrorMsg('El 1XBET ID debe contener únicamente números.');
      return;
    }
    if (clean.length < 3) {
      setErrorMsg('El 1XBET ID debe tener al menos 3 dígitos.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    setErrorMsg('');
    setSuccessMsg('');
    setLoading(true);

    const clean = String(inputValue || '').trim();
    try {
      const res = await saveXbetId(currentUser, clean);
      if (res.success || res.xbet_id) {
        const savedId = res.xbet_id || clean;
        setCurrentId(savedId);
        setInputValue(savedId);
        setSuccessMsg('✅ ¡1XBET ID verificado y registrado exitosamente!');
        if (onVerified) onVerified(savedId);
      } else {
        throw new Error(res.error || 'Error al guardar el ID');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error al guardar el 1XBET ID';
      setErrorMsg(msg);
      if (err.response?.data?.alreadySet && err.response?.data?.xbet_id) {
        const idFound = String(err.response.data.xbet_id).trim();
        setCurrentId(idFound);
        setInputValue(idFound);
        if (onVerified) onVerified(idFound);
      }
    } finally {
      setLoading(false);
    }
  };

  const isVerified = Boolean(currentId && String(currentId).trim().length > 0);

  if (fetching) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: '16px',
          background: 'rgba(15, 23, 42, 0.65)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={20} sx={{ color: '#00ff73' }} />
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Verificando 1XBET ID...
        </Typography>
      </Paper>
    );
  }

  // ── ESTADO: VERIFICADO (INMUTABLE) ──
  if (isVerified) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: compact ? 1.5 : 2.5,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(15, 23, 42, 0.85) 100%)',
          border: '1px solid rgba(34, 197, 94, 0.35)',
          boxShadow: '0 4px 20px rgba(34, 197, 94, 0.12)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              background: 'rgba(34, 197, 94, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#4ade80',
              fontSize: '1.4rem',
              flexShrink: 0,
            }}
          >
            <FaCheckCircle />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 800, color: '#f8fafc', fontSize: '0.95rem' }}>
                1XBET ID Verificado
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.2,
                  borderRadius: '6px',
                  backgroundColor: 'rgba(34, 197, 94, 0.15)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  color: '#4ade80',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}
              >
                <FaLock size={10} /> Inmodificable
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.65)', mt: 0.3 }}>
              Tu cuenta está habilitada para reclamar y verificar tipeos.
            </Typography>
          </Box>
        </Box>

        {/* Input deshabilitado que muestra el ID registrado */}
        <Box sx={{ minWidth: { xs: '100%', sm: '210px' } }}>
          <TextField
            value={currentId}
            disabled
            size="small"
            fullWidth
            label="1XBET ID Registrado"
            InputProps={{
              startAdornment: <FaLock style={{ color: '#4ade80', marginRight: 8, fontSize: 13 }} />,
              style: {
                color: '#4ade80',
                fontWeight: 800,
                fontFamily: 'monospace',
                fontSize: '1rem',
                backgroundColor: 'rgba(15, 23, 42, 0.7)',
                borderRadius: '10px',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(34, 197, 94, 0.35) !important',
              },
              '& .MuiInputLabel-root': {
                color: '#4ade80 !important',
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </Paper>
    );
  }

  // ── ESTADO: NO VERIFICADO (DEBE COMPLETAR EL INPUT) ──
  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: compact ? 2 : 2.5,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(15, 23, 42, 0.9) 100%)',
          border: '1px solid rgba(245, 158, 11, 0.45)',
          boxShadow: '0 4px 25px rgba(245, 158, 11, 0.15)',
          mb: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              background: 'rgba(245, 158, 11, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fbbf24',
              fontSize: '1.4rem',
              flexShrink: 0,
            }}
          >
            <FaExclamationTriangle />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 800, color: '#f8fafc', fontSize: '1rem' }}>
                Verificación Obligatoria: 1XBET ID
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: '6px',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#f87171',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}
              >
                Requisito para reclamar
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.75)', mt: 0.5, lineHeight: 1.5 }}>
              Para reclamar cualquier tipeo es indispensable vincular tu <strong>ID de cuenta de 1XBET</strong>.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#fbbf24', fontWeight: 600, mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              ⚠️ <strong>Atención:</strong> Cada 1XBET ID es único por usuario (no puede estar en dos cuentas) y una vez guardado <strong>NO podrá ser modificado</strong>.
            </Typography>
          </Box>
        </Box>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5' }}>
            {errorMsg}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#86efac' }}>
            {successMsg}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleOpenConfirm}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          <TextField
            value={inputValue}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, '');
              setInputValue(onlyNums);
            }}
            disabled={loading}
            placeholder="Ingresá tu 1XBET ID (solo números, ej: 89432190)"
            size="small"
            fullWidth
            type="text"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            InputProps={{
              startAdornment: <FaShieldAlt style={{ color: '#fbbf24', marginRight: 10, fontSize: 14 }} />,
              style: {
                color: '#fff',
                fontFamily: 'Outfit, sans-serif',
                fontSize: '0.95rem',
                backgroundColor: 'rgba(15, 23, 42, 0.8)',
                borderRadius: '10px',
              },
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(245, 158, 11, 0.4) !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(245, 158, 11, 0.7) !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#fbbf24 !important',
              },
            }}
          />
          <Button
            type="submit"
            disabled={loading || !inputValue.trim()}
            variant="contained"
            sx={{
              backgroundColor: '#f59e0b',
              color: '#000',
              fontWeight: 800,
              fontSize: '0.9rem',
              px: 3,
              py: 1,
              borderRadius: '10px',
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(245, 158, 11, 0.35)',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#fbbf24',
                boxShadow: '0 4px 20px rgba(245, 158, 11, 0.5)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Guardar 1XBET ID'}
          </Button>
        </Box>
      </Paper>

      {/* Modal de confirmación final para garantizar inmutabilidad */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#0f172a',
            color: '#fff',
            borderRadius: '16px',
            border: '1px solid rgba(245, 158, 11, 0.4)',
            p: 1,
            maxWidth: '460px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 1 }}>
          <FaLock /> Confirmar 1XBET ID
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
            ¿Estás completamente seguro de que tu ID de cuenta en 1XBET es el siguiente?
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              border: '1px dashed #f59e0b',
              textAlign: 'center',
              mb: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: '#fbbf24', textTransform: 'uppercase', fontWeight: 700 }}>
              Tu 1XBET ID
            </Typography>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900, fontFamily: 'monospace', mt: 0.5 }}>
              {inputValue.trim()}
            </Typography>
          </Box>
          <DialogContentText sx={{ color: '#f87171', fontSize: '0.85rem', fontWeight: 600 }}>
            ⚠️ Recordá: Cada 1XBET ID es único por usuario y no puede estar registrado en dos cuentas. Una vez guardado, este ID NO se podrá modificar bajo ninguna circunstancia. Si ingresás un ID erróneo, tus solicitudes de tipeo serán rechazadas.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancelar y revisar
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            sx={{
              backgroundColor: '#f59e0b',
              color: '#000',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#fbbf24' },
            }}
          >
            Sí, guardar permanentemente
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
